import type { DeviceCapabilities } from './device.js';
import { cachedCalibration, calibratedTier, startScaleForTier, type GraphicsCalibration } from './graphics-calibration.js';
import { browserLocalStorage, CodecRepository, type Codec } from './persistence.js';

export type GraphicsQuality = 'auto' | 'low' | 'medium' | 'high' | 'ultra';
export type FrameRateTarget = 'smooth' | 'battery';
export type MobileControlLayout = 'standard' | 'left-handed';
export type SteeringAssistMode = 'arcade' | 'balanced' | 'pro';
export type MotionResponseCurve = 'precise' | 'linear' | 'agile';
export type AudioDynamicsSetting = 'wide' | 'standard' | 'night';

export interface GameSettings {
  graphicsQuality: GraphicsQuality;
  bloom: boolean;
  shadows: boolean;
  adaptivePerformance: boolean;
  personalGhost: boolean;
  /** 0..1 strength of impact and landing camera shake. */
  cameraShake: number;
  /** Dims explosion flashes and blast lights for photosensitive players. */
  softFlashes: boolean;
  /** Heat shimmer over fires and refracting blast shock fronts in the post pass. */
  screenDistortion: boolean;
  /** Screen-space ambient occlusion and wet-road reflections (high/ultra only). */
  screenSpaceLighting: boolean;
  /** Cars reflect their live surroundings through a cube probe (high/ultra only). */
  carReflections: boolean;
  frameRateTarget: FrameRateTarget;
  motionSteeringEnabled: boolean;
  motionSensitivity: number;
  motionDeadZone: number;
  motionResponseCurve: MotionResponseCurve;
  mobileControlLayout: MobileControlLayout;
  mobileControlSize: number;
  mobileControlOpacity: number;
  mobileHaptics: boolean;
  steeringAssistMode: SteeringAssistMode;
  /** Kept in storage for backwards compatibility with v2 settings. */
  mobileSteeringAssist: boolean;
  mobileAutoThrottle: boolean;
  drivingCoach: boolean;
  /** Start every race from the driver's seat; the in-race view toggle keeps it in sync. */
  cockpitView: boolean;
  masterVolume: number;
  musicVolume: number;
  effectsVolume: number;
  /** Menu, pause-screen and reward cues. */
  uiVolume: number;
  /** Mastering preset: Wide keeps transients, Night compresses for speakers. */
  audioDynamics: AudioDynamicsSetting;
  /** Inner steering dead zone for controller sticks, 0..0.4 of travel. */
  gamepadSteerDeadZone: number;
  /** Controller steering response exponent: <1 quick, 1 linear, >1 precise. */
  gamepadSteerLinearity: number;
  /** Trigger creep filter, 0..0.3 of travel. */
  gamepadTriggerDeadZone: number;
  /** Controller rumble strength, 0 disables it. */
  gamepadVibration: number;
}

export interface GraphicsProfile {
  quality: Exclude<GraphicsQuality, 'auto'>;
  pixelRatioCap: number;
  bloomStrength: number;
  shadowType: 'basic' | 'pcf' | 'soft';
  shadowMapSize: number;
  /** Hardware MSAA samples for the post-processed scene; 0 keeps the shader edge filter only. */
  msaaSamples: number;
}

export const DEFAULT_SETTINGS: GameSettings = {
  graphicsQuality: 'auto',
  bloom: true,
  shadows: true,
  adaptivePerformance: true,
  personalGhost: true,
  cameraShake: 1,
  softFlashes: false,
  screenDistortion: true,
  screenSpaceLighting: true,
  carReflections: true,
  frameRateTarget: 'smooth',
  motionSteeringEnabled: true,
  motionSensitivity: 1,
  motionDeadZone: 2,
  motionResponseCurve: 'linear',
  mobileControlLayout: 'standard',
  mobileControlSize: 1,
  mobileControlOpacity: 0.9,
  mobileHaptics: true,
  steeringAssistMode: 'arcade',
  mobileSteeringAssist: true,
  mobileAutoThrottle: false,
  drivingCoach: true,
  cockpitView: false,
  masterVolume: 1,
  musicVolume: 1,
  effectsVolume: 1,
  uiVolume: 0.8,
  audioDynamics: 'standard',
  gamepadSteerDeadZone: 0.14,
  gamepadSteerLinearity: 1,
  gamepadTriggerDeadZone: 0.06,
  gamepadVibration: 1,
};

const STORAGE_KEY = 'nightshift:settings';
const SETTINGS_STORAGE_VERSION = 2;
const QUALITIES = new Set<GraphicsQuality>(['auto', 'low', 'medium', 'high', 'ultra']);
const FRAME_RATE_TARGETS = new Set<FrameRateTarget>(['smooth', 'battery']);
const MOBILE_LAYOUTS = new Set<MobileControlLayout>(['standard', 'left-handed']);
const STEERING_ASSIST_MODES = new Set<SteeringAssistMode>(['arcade', 'balanced', 'pro']);
const MOTION_RESPONSE_CURVES = new Set<MotionResponseCurve>(['precise', 'linear', 'agile']);
const AUDIO_DYNAMICS = new Set<AudioDynamicsSetting>(['wide', 'standard', 'night']);

export function defaultGameSettings(capabilities?: DeviceCapabilities): GameSettings {
  if (!capabilities) return { ...DEFAULT_SETTINGS };
  const calibration = cachedCalibration();
  // A saved performance test is this device's default graphics setup.
  if (calibration?.status === 'measured') return applyCalibrationToSettings({ ...DEFAULT_SETTINGS }, calibration);
  return {
    ...DEFAULT_SETTINGS,
    ...graphicsDefaults('auto', capabilities),
  };
}

export function loadGameSettings(capabilities?: DeviceCapabilities): GameSettings {
  return settingsRepository(capabilities).load() ?? defaultGameSettings(capabilities);
}

export function saveGameSettings(settings: GameSettings): void {
  // The active session still uses the chosen settings if private mode rejects
  // the repository write.
  settingsRepository().save(settings);
}

export function parseGameSettings(raw: string | null, capabilities?: DeviceCapabilities): GameSettings {
  const defaults = defaultGameSettings(capabilities);
  if (!raw) return defaults;
  try {
    const candidate = JSON.parse(raw) as Partial<GameSettings> & { version?: number };
    const graphicsQuality = QUALITIES.has(candidate.graphicsQuality as GraphicsQuality)
      ? candidate.graphicsQuality as GraphicsQuality
      : defaults.graphicsQuality;
    const graphicsFallback = capabilities
      ? graphicsDefaults(graphicsQuality, capabilities)
      : {
        bloom: defaults.bloom,
        shadows: defaults.shadows,
        screenSpaceLighting: defaults.screenSpaceLighting,
        carReflections: defaults.carReflections,
      };
    const steeringAssistMode = STEERING_ASSIST_MODES.has(candidate.steeringAssistMode as SteeringAssistMode)
      ? candidate.steeringAssistMode as SteeringAssistMode
      : candidate.mobileSteeringAssist === false ? 'pro' : defaults.steeringAssistMode;
    return {
      graphicsQuality,
      bloom: typeof candidate.bloom === 'boolean' ? candidate.bloom : graphicsFallback.bloom,
      shadows: typeof candidate.shadows === 'boolean' ? candidate.shadows : graphicsFallback.shadows,
      adaptivePerformance: typeof candidate.adaptivePerformance === 'boolean'
        ? candidate.adaptivePerformance
        : defaults.adaptivePerformance,
      personalGhost: typeof candidate.personalGhost === 'boolean'
        ? candidate.personalGhost
        : defaults.personalGhost,
      cameraShake: clampNumber(candidate.cameraShake, 0, 1, defaults.cameraShake),
      softFlashes: typeof candidate.softFlashes === 'boolean' ? candidate.softFlashes : defaults.softFlashes,
      screenDistortion: typeof candidate.screenDistortion === 'boolean' ? candidate.screenDistortion : defaults.screenDistortion,
      screenSpaceLighting: typeof candidate.screenSpaceLighting === 'boolean'
        ? candidate.screenSpaceLighting
        : graphicsFallback.screenSpaceLighting,
      carReflections: typeof candidate.carReflections === 'boolean'
        ? candidate.carReflections
        : graphicsFallback.carReflections,
      frameRateTarget: FRAME_RATE_TARGETS.has(candidate.frameRateTarget as FrameRateTarget)
        ? candidate.frameRateTarget as FrameRateTarget
        : defaults.frameRateTarget,
      // Builds before v2 could persist the temporary Touch fallback as the
      // phone's permanent default. Migrate that legacy value once; v2 still
      // remembers an explicit choice made after this fix.
      motionSteeringEnabled: candidate.version === SETTINGS_STORAGE_VERSION
        && typeof candidate.motionSteeringEnabled === 'boolean'
        ? candidate.motionSteeringEnabled
        : defaults.motionSteeringEnabled,
      motionSensitivity: Number.isFinite(candidate.motionSensitivity)
        ? Math.max(0.6, Math.min(1.8, Number(candidate.motionSensitivity)))
        : defaults.motionSensitivity,
      motionDeadZone: clampNumber(candidate.motionDeadZone, 1.2, 6, defaults.motionDeadZone),
      motionResponseCurve: MOTION_RESPONSE_CURVES.has(candidate.motionResponseCurve as MotionResponseCurve)
        ? candidate.motionResponseCurve as MotionResponseCurve
        : defaults.motionResponseCurve,
      mobileControlLayout: MOBILE_LAYOUTS.has(candidate.mobileControlLayout as MobileControlLayout)
        ? candidate.mobileControlLayout as MobileControlLayout
        : defaults.mobileControlLayout,
      mobileControlSize: clampNumber(candidate.mobileControlSize, 0.86, 1.08, defaults.mobileControlSize),
      mobileControlOpacity: clampNumber(candidate.mobileControlOpacity, 0.55, 1, defaults.mobileControlOpacity),
      mobileHaptics: typeof candidate.mobileHaptics === 'boolean'
        ? candidate.mobileHaptics
        : defaults.mobileHaptics,
      steeringAssistMode,
      mobileSteeringAssist: steeringAssistMode !== 'pro',
      mobileAutoThrottle: typeof candidate.mobileAutoThrottle === 'boolean'
        ? candidate.mobileAutoThrottle
        : defaults.mobileAutoThrottle,
      drivingCoach: typeof candidate.drivingCoach === 'boolean'
        ? candidate.drivingCoach
        : defaults.drivingCoach,
      cockpitView: typeof candidate.cockpitView === 'boolean'
        ? candidate.cockpitView
        : defaults.cockpitView,
      masterVolume: clampNumber(candidate.masterVolume, 0, 1, defaults.masterVolume),
      musicVolume: clampNumber(candidate.musicVolume, 0, 1, defaults.musicVolume),
      effectsVolume: clampNumber(candidate.effectsVolume, 0, 1, defaults.effectsVolume),
      uiVolume: clampNumber(candidate.uiVolume, 0, 1, defaults.uiVolume),
      audioDynamics: AUDIO_DYNAMICS.has(candidate.audioDynamics as AudioDynamicsSetting)
        ? candidate.audioDynamics as AudioDynamicsSetting
        : defaults.audioDynamics,
      gamepadSteerDeadZone: clampNumber(candidate.gamepadSteerDeadZone, 0, 0.4, defaults.gamepadSteerDeadZone),
      gamepadSteerLinearity: clampNumber(candidate.gamepadSteerLinearity, 0.5, 2.2, defaults.gamepadSteerLinearity),
      gamepadTriggerDeadZone: clampNumber(candidate.gamepadTriggerDeadZone, 0, 0.3, defaults.gamepadTriggerDeadZone),
      gamepadVibration: clampNumber(candidate.gamepadVibration, 0, 1, defaults.gamepadVibration),
    };
  } catch {
    return defaults;
  }
}

export type GraphicsFeatureSettings = Pick<GameSettings, 'bloom' | 'shadows' | 'screenSpaceLighting' | 'carReflections'>;

export function graphicsDefaults(quality: GraphicsQuality, capabilities: DeviceCapabilities): GraphicsFeatureSettings {
  const resolved = resolveGraphicsProfile(quality, capabilities);
  const premium = resolved.quality === 'high' || resolved.quality === 'ultra';
  return {
    bloom: resolved.quality !== 'low',
    shadows: premium,
    screenSpaceLighting: premium,
    carReflections: premium,
  };
}

/**
 * Settings after a performance test: AUTO now means the measured tier, and
 * each optional feature follows the measured headroom. Accessibility and
 * control preferences are untouched.
 */
export function applyCalibrationToSettings(settings: GameSettings, calibration: GraphicsCalibration): GameSettings {
  return {
    ...settings,
    graphicsQuality: 'auto',
    adaptivePerformance: true,
    frameRateTarget: calibration.frameRateTarget,
    bloom: calibration.features.bloom,
    shadows: calibration.features.shadows,
    screenDistortion: calibration.features.screenDistortion,
    screenSpaceLighting: calibration.features.screenSpaceLighting,
    carReflections: calibration.features.carReflections,
  };
}

export function resolveGraphicsProfile(
  quality: GraphicsQuality,
  capabilities: DeviceCapabilities,
  hardwareConcurrency = readHardwareConcurrency(capabilities),
  deviceMemory = readDeviceMemory(capabilities),
  chromiumMajor = readChromiumMajor(),
): GraphicsProfile {
  let resolved = quality;
  if (resolved === 'auto' && autoTier()) resolved = autoTier()!;
  if (resolved === 'auto') {
    if (capabilities.phone || capabilities.tablet) {
      // deviceMemory is frequently absent on old Chrome/WebView builds. The
      // fallback below deliberately resolves those touch devices to LOW; an
      // unknown mobile GPU must not be treated like an 8 GB desktop GPU.
      const legacyChromium = chromiumMajor !== null && chromiumMajor < 110;
      resolved = legacyChromium || deviceMemory <= 4 || hardwareConcurrency <= 4 ? 'low' : 'medium';
    } else if (deviceMemory <= 4 || hardwareConcurrency <= 4) resolved = 'low';
    else if (deviceMemory <= 6 || hardwareConcurrency <= 6) resolved = 'medium';
    else resolved = deviceMemory >= 16 && hardwareConcurrency >= 16 ? 'ultra' : 'high';
  }
  return GRAPHICS_PROFILES[resolved];
}

/** Shared, frozen presets: resolveGraphicsProfile runs inside the render loop. */
export const GRAPHICS_PROFILES: Readonly<Record<Exclude<GraphicsQuality, 'auto'>, Readonly<GraphicsProfile>>> = Object.freeze({
  low: Object.freeze({ quality: 'low', pixelRatioCap: 1, bloomStrength: 0.45, shadowType: 'basic', shadowMapSize: 768, msaaSamples: 0 }),
  medium: Object.freeze({ quality: 'medium', pixelRatioCap: 1.25, bloomStrength: 0.72, shadowType: 'pcf', shadowMapSize: 1_024, msaaSamples: 0 }),
  high: Object.freeze({ quality: 'high', pixelRatioCap: 1.55, bloomStrength: 1, shadowType: 'pcf', shadowMapSize: 1_280, msaaSamples: 4 }),
  ultra: Object.freeze({ quality: 'ultra', pixelRatioCap: 1.85, bloomStrength: 1.18, shadowType: 'soft', shadowMapSize: 1_536, msaaSamples: 4 }),
});

let autoTierSource: () => Exclude<GraphicsQuality, 'auto'> | null = () => calibratedTier();

function autoTier(): Exclude<GraphicsQuality, 'auto'> | null {
  return autoTierSource();
}

/** Tests replace the calibration lookup; pass null to restore storage-backed lookup. */
export function setAutoTierSourceForTests(source: (() => Exclude<GraphicsQuality, 'auto'> | null) | null): void {
  autoTierSource = source ?? (() => calibratedTier());
}

/** Measured starting resolution for the active preset, or 1 when untested or not adaptive. */
export function calibratedStartScale(settings: Pick<GameSettings, 'graphicsQuality' | 'adaptivePerformance'>): number {
  if (!settings.adaptivePerformance) return 1;
  const calibration = cachedCalibration();
  if (calibration?.status !== 'measured') return 1;
  // A preset chosen over the recommendation starts where the test says it holds 60.
  const tier = settings.graphicsQuality === 'auto' ? calibration.tier : settings.graphicsQuality;
  return startScaleForTier(calibration, tier) ?? 1;
}

/** Whether AUTO resolves from a performance test rather than the hardware heuristic. */
export function autoTierIsMeasured(): boolean {
  return autoTier() !== null;
}

/** Start weak mobile hardware smoothly instead of spending ~10 bad seconds discovering it. */
export function shouldStartWithConservativeFrameRate(
  settings: GameSettings,
  capabilities: DeviceCapabilities,
): boolean {
  return settings.adaptivePerformance
    && settings.frameRateTarget === 'smooth'
    && capabilities.touchDevice
    && resolveGraphicsProfile(settings.graphicsQuality, capabilities).quality === 'low';
}

function readHardwareConcurrency(capabilities: DeviceCapabilities): number {
  if (typeof navigator === 'undefined') return capabilities.touchDevice ? 4 : 8;
  return navigator.hardwareConcurrency || (capabilities.touchDevice ? 4 : 8);
}

function readDeviceMemory(capabilities: DeviceCapabilities): number {
  if (typeof navigator === 'undefined') return capabilities.touchDevice ? 4 : 8;
  return (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? (capabilities.touchDevice ? 4 : 8);
}

function readChromiumMajor(): number | null {
  if (typeof navigator === 'undefined') return null;
  const match = navigator.userAgent.match(/(?:Chrome|Chromium)\/(\d+)/i);
  if (!match?.[1]) return null;
  const major = Number(match[1]);
  return Number.isFinite(major) ? major : null;
}

function clampNumber(value: unknown, minimum: number, maximum: number, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(minimum, Math.min(maximum, parsed)) : fallback;
}

function settingsRepository(capabilities?: DeviceCapabilities): CodecRepository<GameSettings> {
  const codec: Codec<GameSettings> = {
    encode: (settings) => JSON.stringify({ ...settings, version: SETTINGS_STORAGE_VERSION }),
    decode: (raw) => parseGameSettings(raw, capabilities),
  };
  return new CodecRepository(browserLocalStorage(), STORAGE_KEY, codec);
}
